import { memo, useRef, useEffect, useCallback } from "react";

interface VideoPlayerProps {
	src: string;
	seekTo: { time: number } | null;
	onDurationLoaded: (duration: number) => void;
	onTimeUpdate: (time: number) => void;
}

export const VideoPlayer = memo(function VideoPlayer({
	src,
	seekTo,
	onDurationLoaded,
	onTimeUpdate,
}: VideoPlayerProps) {
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		if (videoRef.current && seekTo !== null) {
			videoRef.current.currentTime = seekTo.time;
			onTimeUpdate(videoRef.current.currentTime);
		}
	}, [onTimeUpdate, seekTo]);

	const handleLoadedMetadata = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;
		onDurationLoaded(video.duration);
		onTimeUpdate(video.currentTime);
	}, [onDurationLoaded, onTimeUpdate]);

	const handleTimeUpdate = useCallback(
		(event: React.SyntheticEvent<HTMLVideoElement>) => {
			onTimeUpdate(event.currentTarget.currentTime);
		},
		[onTimeUpdate],
	);

	if (!src) {
		return (
			<div className="flex min-h-0 flex-1 items-center justify-center rounded-4 bg-fixed-black text-caption text-fg-3">
				動画が見つかりません
			</div>
		);
	}

	// 下部ペインの高さは可変なので、余った領域いっぱいに置いて object-contain で内側に収める
	// (縦動画でも横幅を奪わず、寸法判明時のレイアウトシフトも起きない)。
	return (
		<video
			ref={videoRef}
			src={src}
			onLoadedMetadata={handleLoadedMetadata}
			onTimeUpdate={handleTimeUpdate}
			controls
			className="min-h-0 w-full flex-1 rounded-4 bg-fixed-black object-contain"
			data-testid="video-player"
		/>
	);
});
